import { useMemo } from 'react';

import { useGetCapabilities } from '@/hooks/api/useGetCapabilities';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useStatisticsBatch } from '@/hooks/useStatisticsBatch';
import { SeedStatus, SeedStatusLabel } from '@/types';
import type { DropdownOption } from '@/types/dropdown.types';
import { formatPrettyName } from '@/utils/mappings.util';
import {
  isValidBasicValue,
  normalizeCountryValue,
  normalizeEmailValue,
} from '@/utils/statisticValidation.util';

// Type for filter options
type FilterOption = DropdownOption & {
  key?: string;
};

// Type for grouped filter options (matches Assets pattern)
type GroupOption = FilterOption & {
  isGroup?: boolean;
  items?: FilterOption[];
};

export const useGetSeedFilters = (): {
  data: Record<string, (FilterOption | GroupOption)[]>;
  status: 'error' | 'success' | 'pending';
} => {
  // Fetch capabilities to determine integrations vs Chariot capabilities
  const {
    integrations,
    chariotCapabilities,
    status: capabilitiesStatus,
  } = useGetCapabilities();

  // Batch fetch all seed statistics in a single request (9 queries → 1 request)
  // Performance: 90-97% faster than individual queries
  // Note: seed#domain#name, seed#ip#name, and class#seed removed as filters were deprecated
  const batchStatistics = useStatisticsBatch({
    statisticKeys: [
      'seed#domain#origin',
      'seed#domain#email',
      'seed#domain#registrar',
      'seed#domain#registrant',
      'seed#ip#origin',
      'seed#ip',
      'seed#ip#registrar',
      'seed#ip#registrant',
      'seed#tags',
    ],
    enabled: true,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Extract individual statistics from batch response (memoized to prevent hooks order issues)
  const extractedStats = useMemo(
    () => ({
      domainSourceStats: batchStatistics.data?.['seed#domain#origin'] || [],
      domainEmailStats: batchStatistics.data?.['seed#domain#email'] || [],
      domainRegistrarStats:
        batchStatistics.data?.['seed#domain#registrar'] || [],
      domainRegistrantStats:
        batchStatistics.data?.['seed#domain#registrant'] || [],
      ipSourceStats: batchStatistics.data?.['seed#ip#origin'] || [],
      ipCountryStats: batchStatistics.data?.['seed#ip'] || [],
      ipRegistrarStats: batchStatistics.data?.['seed#ip#registrar'] || [],
      ipRegistrantStats: batchStatistics.data?.['seed#ip#registrant'] || [],
      seedTagStats: batchStatistics.data?.['seed#tags'] || [],
    }),
    [batchStatistics.data]
  );

  const {
    domainSourceStats,
    domainEmailStats,
    domainRegistrarStats,
    domainRegistrantStats,
    ipSourceStats,
    ipCountryStats,
    ipRegistrarStats,
    ipRegistrantStats,
    seedTagStats,
  } = extractedStats;

  // Map source options - combine domain and IP source statistics with Chariot grouping
  const source = useMemo(() => {
    if (capabilitiesStatus === 'pending') return [];

    const presentSources = [
      ...(domainSourceStats || []),
      ...(ipSourceStats || []),
    ]
      .filter(isValidBasicValue)
      .map(stat => String(stat.value));

    const chariotSources: FilterOption[] = [];
    const integrationSources: FilterOption[] = [];

    // Add special Chariot sources that aren't capabilities
    chariotSources.push(
      { label: 'Manual', value: '@praetorian.com', id: '@praetorian.com' },
      { label: 'Chariot', value: 'chariot', id: 'chariot' },
      { label: 'Provided', value: 'provided', id: 'provided' },
      { label: 'Other', value: 'adhoc', id: 'adhoc' }
    );

    // Add Chariot capabilities that are present in the source stats
    chariotCapabilities.forEach(capability => {
      const capabilityName = capability.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(capabilityName)) {
        // Find the exact value from presentSources for this capability
        const exactValue =
          presentSources.find(s => s.toLowerCase() === capabilityName) ||
          capability.name;

        // Create a display label from the capability name
        const displayLabel = capability.title
          ? capability.title
          : capability.name
              .split(/[-_]/)
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

        chariotSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    // Process integrations from the integrations array
    integrations.forEach(integration => {
      const integrationName = integration.name.toLowerCase();
      if (presentSources.map(s => s.toLowerCase()).includes(integrationName)) {
        // Find the exact value from presentSources for this integration
        const exactValue =
          presentSources.find(s => s.toLowerCase() === integrationName) ||
          integration.name;

        // Use the integration name as display label, with proper capitalization
        const displayLabel = integration.name
          .split(/[-_]/)
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

        integrationSources.push({
          label: displayLabel,
          value: exactValue,
          id: exactValue,
        });
      }
    });

    chariotSources.sort((a, b) => a.label.localeCompare(b.label));
    integrationSources.sort((a, b) => a.label.localeCompare(b.label));

    const result: (FilterOption | GroupOption)[] = [];

    if (chariotSources.length > 0) {
      result.push({
        label: 'Praetorian',
        value: 'chariot',
        id: 'chariot',
        isGroup: true,
        items: chariotSources,
      });
    }

    result.push(...integrationSources);

    return result;
  }, [
    domainSourceStats,
    ipSourceStats,
    integrations,
    chariotCapabilities,
    capabilitiesStatus,
  ]);

  // Map email options - only domain seeds have email
  const email = useMemo(() => {
    if (!domainEmailStats) return [];

    const allEmails = new Set<string>();

    domainEmailStats
      .filter(isValidBasicValue) // Filter out invalid basic values
      .forEach(stat => {
        const normalized = normalizeEmailValue(String(stat.value));
        // Only add if validation passed (not null)
        if (normalized) {
          allEmails.add(normalized);
        }
      });

    return Array.from(allEmails)
      .map(value => ({
        id: value,
        value: value, // Already normalized (lowercase, trimmed)
        label: value, // Already normalized (lowercase, trimmed)
      }))
      .sort((a: FilterOption, b: FilterOption) =>
        (a.label || '').localeCompare(b.label || '')
      );
  }, [domainEmailStats]);

  // Map country options - only IP seeds have country
  const country = useMemo(() => {
    if (!ipCountryStats) return [];

    const allCountries = new Set<string>();

    ipCountryStats
      .filter(stat => stat.name === 'country' && isValidBasicValue(stat))
      .forEach(stat => allCountries.add(String(stat.value)));

    return Array.from(allCountries)
      .map(value => ({
        id: value,
        value: value, // Keep raw value for filtering
        label: normalizeCountryValue(value), // Normalize display (e.g., "united states" → "US")
      }))
      .sort((a: FilterOption, b: FilterOption) =>
        normalizeCountryValue(String(a.value || '')).localeCompare(
          normalizeCountryValue(String(b.value || ''))
        )
      );
  }, [ipCountryStats]);

  // Map registrar options - combine domain and IP registrar statistics
  const registrar = useMemo(() => {
    const allRegistrars = new Set<string>();

    [...(domainRegistrarStats || []), ...(ipRegistrarStats || [])]
      .filter(isValidBasicValue)
      .filter(stat => {
        // Additional validation: filter out extremely long values (likely corrupted data)
        const val = String(stat.value);
        return val.length < 100; // Registrar names shouldn't be this long
      })
      .forEach(stat => allRegistrars.add(String(stat.value)));

    return Array.from(allRegistrars)
      .map(value => ({
        id: value,
        value: value,
        label: formatPrettyName(
          value.length > 50 ? value.substring(0, 47) + '...' : value
        ),
      }))
      .sort((a: FilterOption, b: FilterOption) =>
        (a.label || '').localeCompare(b.label || '')
      );
  }, [domainRegistrarStats, ipRegistrarStats]);

  // Map registrant options - combine domain and IP registrant statistics
  const registrant = useMemo(() => {
    const allRegistrants = new Set<string>();

    [...(domainRegistrantStats || []), ...(ipRegistrantStats || [])]
      .filter(isValidBasicValue)
      .filter(stat => {
        // Additional validation: filter out extremely long values (likely corrupted data)
        const val = String(stat.value);
        return val.length < 100; // Registrant names shouldn't be this long
      })
      .forEach(stat => allRegistrants.add(String(stat.value)));

    return Array.from(allRegistrants)
      .map(value => ({
        id: value,
        value: value,
        label: formatPrettyName(
          value.length > 50 ? value.substring(0, 47) + '...' : value
        ),
      }))
      .sort((a: FilterOption, b: FilterOption) =>
        (a.label || '').localeCompare(b.label || '')
      );
  }, [domainRegistrantStats, ipRegistrantStats]);

  // Map tags options from seed tag statistics
  const tags = useMemo(() => {
    if (!seedTagStats) return [];

    const allTags = new Set<string>();

    seedTagStats
      .filter(isValidBasicValue)
      .filter(stat => {
        // Validate tag values
        const val = String(stat.value);
        return val.length > 0 && val.length < 100;
      })
      .forEach(stat => allTags.add(String(stat.value)));

    return Array.from(allTags)
      .map(value => ({
        id: value,
        value: value,
        label: value,
      }))
      .sort((a: FilterOption, b: FilterOption) =>
        (a.label || '').localeCompare(b.label || '')
      );
  }, [seedTagStats]);

  // Status values are static - seeds have different status values than other entities
  const status = useMemo(
    () =>
      Object.values(SeedStatus)
        .filter(
          value =>
            value !== SeedStatus.PendingLow && value !== SeedStatus.AcceptLow
        )
        .map(value => ({
          id: value,
          value: value,
          label: SeedStatusLabel[value],
        })),
    []
  );

  // Scan profile filter options (client-side derived from settings)
  const { profiles: scanProfiles } = useScanProfiles();
  const profile = useMemo(
    () =>
      scanProfiles.length > 0
        ? [
            {
              id: 'profile-default',
              label: 'Default (No Profile)',
              value: '',
            } as FilterOption,
            ...scanProfiles.map(
              (p, index) =>
                ({
                  id: `profile-${p.id}-${index}`,
                  label: p.name,
                  value: p.id,
                }) as FilterOption
            ),
          ]
        : [],
    [scanProfiles]
  );

  const options = {
    origins: source,
    email,
    country,
    status,
    registrar,
    registrant,
    tags,
    ...(profile.length > 0 ? { profile } : {}),
  };

  // Merge status - simplified with batch statistics (12 queries → 1)
  const mergedStatus = [batchStatistics.status, capabilitiesStatus].includes(
    'pending'
  )
    ? 'pending'
    : [batchStatistics.status, capabilitiesStatus].includes('error')
      ? 'error'
      : 'success';

  return { data: options, status: mergedStatus };
};
